import { useState, useEffect, useCallback } from 'react';
import { profileService } from '../services/profileService';
import { UserProfile, UpdateProfileRequest } from '../types/profileTypes';
import { useAuth } from '../../../hooks/useAuth';

export const useProfile = () => {
  const { authState } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!authState.user?.username) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await profileService.getProfileByUsername(authState.user.username);
      
      if (result.success && result.data) {
        setProfile(result.data);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to fetch profile');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [authState.user?.username]);

  const createProfile = useCallback(async (profileData: UpdateProfileRequest) => {
    if (!authState.user?.username) {
      throw new Error('User not authenticated');
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const request = {
        data: {
          username: authState.user.username,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          profile_type: profileData.profile_type || 'individual',
          ...profileData
        }
      };
      
      const result = await profileService.insert(request);
      
      if (result.success && result.data) {
        setProfile(result.data);
        
        // Immediately fetch the profile to verify it was saved correctly
        await fetchProfile();
        
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to create profile');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [authState.user?.username, fetchProfile]);

  const updateProfile = useCallback(async (updates: UpdateProfileRequest) => {
    if (!authState.user?.username) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await profileService.updateProfileByUsername(authState.user.username, updates);
      if (result.success && result.data) {
        setProfile(result.data);
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to update profile');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [authState.user?.username]);

  const uploadAvatar = useCallback(async (file: File) => {
    if (!authState.user?.username) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      // For now, we'll simulate avatar upload by returning a placeholder URL
      // In a real implementation, this would upload to a file service
      const avatarUrl = URL.createObjectURL(file);
      
      // Update profile with new avatar URL in contact_details
      const updates: UpdateProfileRequest = {
        contact_details: {
          ...profile?.contact_details,
          avatar_url: avatarUrl
        }
      };
      
      const result = await profileService.updateProfileByUsername(authState.user.username, updates);
      if (result.success && result.data) {
        setProfile(result.data);
      }
      
      return avatarUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload avatar';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [authState.user?.username, profile]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    fetchProfile,
    createProfile,
    updateProfile,
    uploadAvatar,
    refetch: fetchProfile
  };
};